import { reviveQueryDates } from 'ts-core';
import type { EmbedServerConfig } from 'ts-core';
import { BalloonEventsMapInstance } from '../core/BalloonEventsMapInstance';
import { BalloonEventsMapOptions } from '../app/types/EmbedOptions';
import { fetchEmbedConfig } from './embedConfig';
import "../style.css";
/**
 * Web Component (Custom Element) for BalloonEventsMap.
 * Provides framework-agnostic usage via HTML tag.
 * 
 * @example
 * ```html
 * <balloon-events-map
 *   api-key="your-key"
 *   language="de"
 *   initial-lat="48.2082"
 *   initial-lng="16.3738"
 * ></balloon-events-map>
 * 
 * <script>
 *   document.querySelector('balloon-events-map')
 *     .addEventListener('ready', (e) => console.log(e.detail));
 * </script>
 * ```
 */
export class BalloonEventsMapElement extends HTMLElement {
  private instance: BalloonEventsMapInstance | null = null;
  private mountObserver: MutationObserver | null = null;
  private initializing = false;
  private configAbortController: AbortController | null = null;
  private injectedStyleEl: HTMLStyleElement | null = null;

  /**
   * List of attributes to observe for changes
   */
  static get observedAttributes(): string[] {
    return [
      'api-key',
      'language',
      'initial-lat',
      'initial-lng',
      'initial-zoom',
      'map-scrollable',
      'show-bookmark-button',
      'show-bottom-cards',
      'preview-cards',
      'show-zeppelins',
      'show-debug',
      'show-creator-link',
      'cooperative-gestures',
      'routing-type',
      'root-query',
      'mobile-root-query',
    ];
  }

  /**
   * Called when element is added to DOM
   */
  connectedCallback(): void {
    // Wait for element to be fully in DOM before rendering
    if (document.contains(this)) {
      void this.initialize();
    } else {
      // Observe until element is in DOM
      this.mountObserver = new MutationObserver(() => {
        if (document.contains(this)) {
          void this.initialize();
          this.mountObserver?.disconnect();
        }
      });
      this.mountObserver.observe(document, { childList: true, subtree: true });
    }
  }

  /**
   * Called when element is removed from DOM
   */
  disconnectedCallback(): void {
    this.mountObserver?.disconnect();
    this.configAbortController?.abort('Element disconnected');
    this.configAbortController = null;
    this.applyInjectedCss(undefined);
    if (this.instance) {
      this.instance.destroy();
      this.instance = null;
    }
  }

  /**
   * Called when observed attribute changes
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue || !this.instance) return;

    // Recreate instance with new options when attributes change
    // Note: This is a full re-render. For better performance, implement
    // partial updates in the future.
    console.log(`[BalloonEventsMapElement] Attribute changed: ${name}`);

    this.instance.destroy();
    this.instance = null;
    void this.initialize();
  }

  /**
   * Initialize the map instance.
   *
   * When an api-key is present, the server-managed embed config is fetched
   * first and wins over the HTML attributes, so Nova embed-tab edits apply
   * on the next page load without re-issuing embed code. Attributes (plus
   * frontend defaults) remain the fallback when the fetch fails.
   */
  private async initialize(): Promise<void> {
    if (this.instance || this.initializing) return;
    this.initializing = true;

    try {
      const partnerKey = this.getAttribute('api-key');
      let serverConfig: EmbedServerConfig | null = null;

      if (partnerKey) {
        this.configAbortController = new AbortController();
        serverConfig = await fetchEmbedConfig(partnerKey, this.configAbortController.signal);
      }

      // Element removed from the DOM while the config was loading
      if (!this.isConnected) return;

      // Parse attributes after the await so writes that happened while the
      // fetch was in flight (ignored by attributeChangedCallback because no
      // instance existed yet) are still picked up.
      const options = this.parseAttributes();

      const { style, injectCss, ...configOptions }: EmbedServerConfig = serverConfig ?? {};
      if (style && style.trim() !== '') {
        // Server style replaces the pasted inline style. `style` is not an
        // observed attribute, so this write cannot re-trigger initialization.
        this.setAttribute('style', style);
      }
      this.applyInjectedCss(injectCss);

      this.instance = new BalloonEventsMapInstance({ ...options, ...configOptions });

      // Forward all instance events to CustomEvents
      this.forwardEvents();

      // Render the map
      this.instance.render(this).catch((error) => {
        console.error('[BalloonEventsMapElement] Error rendering map:', error);
        this.dispatchEvent(
          new CustomEvent('error', {
            detail: { error },
            bubbles: true,
          })
        );
      });
    } catch (error) {
      console.error('[BalloonEventsMapElement] Initialization error:', error);
      this.dispatchEvent(
        new CustomEvent('error', {
          detail: { error },
          bubbles: true,
        })
      );
    } finally {
      this.initializing = false;
      this.configAbortController = null;
    }
  }

  /**
   * Insert, update, or remove the admin-authored corrective CSS from the
   * server config as a <style> tag directly before the host element.
   * `textContent` assignment means the CSS is never HTML-parsed, and reusing
   * one element keeps re-initialization from stacking duplicates.
   */
  private applyInjectedCss(css?: string): void {
    if (css && css.trim() !== '') {
      if (!this.injectedStyleEl) {
        this.injectedStyleEl = document.createElement('style');
        this.injectedStyleEl.setAttribute('data-balloon-events-inject-css', '');
      }
      this.injectedStyleEl.textContent = css;
      if (!this.injectedStyleEl.isConnected) {
        this.before(this.injectedStyleEl);
      }
    } else if (this.injectedStyleEl) {
      this.injectedStyleEl.remove();
      this.injectedStyleEl = null;
    }
  }

  /**
   * Parse HTML attributes to options object
   */
  private parseAttributes(): BalloonEventsMapOptions {
    // Embed + mobile defaults to Mapbox's cooperativeGestures: a single-finger
    // swipe scrolls the host page (otherwise the map traps every vertical
    // gesture and the user can never pan past the embed on a phone).
    //
    // We deliberately DO NOT enable it on desktop embeds: Mapbox's desktop
    // mode of cooperativeGestures requires Ctrl/Cmd + wheel to zoom, which is
    // unfamiliar friction in a context where there's no page-scroll conflict
    // to solve in the first place.
    //
    // Match the project's existing "mobile" definition from useIsMobile
    // (window.innerWidth <= 768) rather than `(pointer: coarse)` — keeps the
    // breakpoint consistent with every other mobile-vs-desktop branch in the
    // app, and avoids surprises on hybrid devices (touch laptops).
    //
    // Hosts can still override via `cooperative-gestures="false"` or force it
    // on desktop with `cooperative-gestures="true"`; the explicit-attribute
    // parser below runs after this default and wins.
    const isMobileViewport =
      typeof window !== 'undefined' && window.innerWidth <= 768;

    const options: Partial<BalloonEventsMapOptions> = {
      type: 'map',
      key: this.getAttribute('api-key') || '',
      language: this.getAttribute('language') || 'de',
      cooperativeGestures: isMobileViewport,
    };

    // Parse initial location
    const lat = this.getAttribute('initial-lat');
    const lng = this.getAttribute('initial-lng');
    if (lat && lng) {
      options.initialLocation = {
        lat: parseFloat(lat),
        lng: parseFloat(lng),
      };
    }

    // Parse initial zoom
    const zoom = this.getAttribute('initial-zoom');
    if (zoom) {
      options.initialZoom = parseInt(zoom, 10);
    }

    // Parse boolean attributes
    const booleanAttrs: Record<string, keyof BalloonEventsMapOptions> = {
      'map-scrollable': 'mapScrollable',
      'show-bookmark-button': 'showBookmarkButton',
      'show-bottom-cards': 'showBottomCards',
      'preview-cards': 'previewCards',
      'show-zeppelins': 'showZeppelins',
      'show-debug': 'showDebug',
      'show-creator-link': 'showCreatorLink',
      'cooperative-gestures': 'cooperativeGestures',
    };

    Object.entries(booleanAttrs).forEach(([attr, prop]) => {
      if (this.hasAttribute(attr)) {
        const value = this.getAttribute(attr);
        // Support both boolean attributes (<tag attr>) and explicit values (<tag attr="true">)
        (options as any)[prop] = value === '' || value === 'true';
      }
    });

    // Parse string attributes
    const routingType = this.getAttribute('routing-type');
    if (routingType) {
      options.routingType = routingType as 'hash' | 'browser';
    }

    // Parse JSON query attributes
    const rootQuery = this.parseJsonAttribute('root-query');
    if (rootQuery) {
      options.rootQuery = rootQuery;
    }
    const mobileRootQuery = this.parseJsonAttribute('mobile-root-query');
    if (mobileRootQuery) {
      options.mobileRootQuery = mobileRootQuery;
    }

    return options as BalloonEventsMapOptions;
  }

  /**
   * Parse a JSON-encoded attribute into a BalloonEventsQuery.
   * Converts ISO date strings inside `dates` back into Date objects.
   */
  private parseJsonAttribute(name: string): any | null {
    const raw = this.getAttribute(name);
    if (!raw) return null;
    try {
      return reviveQueryDates(JSON.parse(raw));
    } catch (error) {
      console.error(`[BalloonEventsMapElement] Failed to parse ${name} as JSON:`, error);
      return null;
    }
  }

  /**
   * Forward instance events to CustomEvents for Web Component API
   */
  private forwardEvents(): void {
    if (!this.instance) return;

    const events = [
      'ready',
      'eventClick',
      'eventHover',
      'locationChange',
      'zoomChange',
      'error',
      'destroyed',
    ];

    events.forEach((event) => {
      this.instance!.on(event, (data: any) => {
        this.dispatchEvent(
          new CustomEvent(event, {
            detail: data,
            bubbles: true,
          })
        );
      });
    });
  }

  /**
   * Get the map instance (for programmatic access)
   */
  public getInstance(): BalloonEventsMapInstance | null {
    return this.instance;
  }
}

// Auto-register the custom element
if (typeof window !== 'undefined' && typeof customElements !== 'undefined') {
  if (!customElements.get('balloon-events-map')) {
    customElements.define('balloon-events-map', BalloonEventsMapElement);
    
    if (import.meta.env.NODE_ENV === 'development') {
      console.log('[BalloonEventsMap] Web Component registered: <balloon-events-map>');
    }
  }
}
